import { useState, useMemo } from 'react'
import EnterpriseIcon, {
  CancelIcon,
  ChevronDownIcon,
  SearchIcon,
} from '@enterprise-ui/icons'
import { autocompleteCustomSearch } from '../../../common/helper'
import {
  CloseButton,
  Drawer,
  DrawerHeader,
  Overlay,
  SideNavActiveItem,
  SideNavExternalLink,
  SideNavSection,
  SideNavTitle,
  SideNavAccountButton,
  SideNavAccountDropdown,
  SideNavAccountItem,
  SideNavSearchContainer,
  SideNavSearchInput,
  SideNavNoResults,
} from './styles/SideNav'
import {
  getVisibleTabs,
  NavigationContext,
  NavigationTab,
} from './ContextualNavigation'

interface Props {
  isOpen: boolean
  onClose: () => void
  context: NavigationContext | null
  isExternalUser: boolean
  isInternalUser: boolean
  pathname: string
  insightsURL?: string
  selectedAccount: any
  accounts: any[]
  onAccountChange: (account: any) => void
  getRmsCampaignURL: () => string
  getRmsBillingURL: () => string
  getPlanningURL: () => string
}

const SideNav = ({
  isOpen,
  onClose,
  context,
  isInternalUser,
  pathname,
  insightsURL,
  selectedAccount,
  accounts,
  onAccountChange,
  getRmsCampaignURL,
  getRmsBillingURL,
  getPlanningURL,
}: Props) => {
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false)
  const [searchText, setSearchText] = useState('')

  // Get visible tabs based on context and user type
  const visibleTabs = getVisibleTabs(context, isInternalUser)

  const getAccountLabel = (account: any) =>
    account?.displayName || account?.name || account?.label || ''

  const displayName = getAccountLabel(selectedAccount) || 'Select Account'

  // Filter accounts using the shared autocomplete search helper
  const filteredAccounts = useMemo(() => {
    if (!searchText.trim()) return accounts || []
    return (accounts || []).filter((account: any) =>
      autocompleteCustomSearch(searchText, { label: getAccountLabel(account) }),
    )
  }, [accounts, searchText])

  const handleAccountSelect = (account: any) => {
    onAccountChange(account)
    setIsAccountDropdownOpen(false)
    setSearchText('')
  }

  // Helper to render tab based on tab name
  const renderTab = (tab: NavigationTab) => {
    switch (tab) {
      case 'Campaigns':
        return (
          <SideNavExternalLink
            key={tab}
            href={getRmsCampaignURL()}
            target="_blank"
            rel="noopener noreferrer"
          >
            Campaigns
          </SideNavExternalLink>
        )
      case 'Reports':
        // Reports is always active - it's the current application
        return (
          <SideNavActiveItem key={tab} to={'/'} onClick={onClose}>
            Reports
          </SideNavActiveItem>
        )
      case 'Billing':
        return (
          <SideNavExternalLink
            key={tab}
            href={getRmsBillingURL()}
            rel="noopener noreferrer"
          >
            Billing
          </SideNavExternalLink>
        )
      case 'Planning':
        return (
          <SideNavExternalLink
            key={tab}
            href={getPlanningURL()}
            rel="noopener noreferrer"
          >
            Planning
          </SideNavExternalLink>
        )
      case 'Insights':
        return insightsURL ? (
          <SideNavExternalLink key={tab} href={insightsURL}>
            Insights
          </SideNavExternalLink>
        ) : null
      default:
        return null
    }
  }

  return (
    <>
      <Overlay isOpen={isOpen} onClick={onClose} aria-hidden={!isOpen} />
      <Drawer isOpen={isOpen} aria-hidden={!isOpen}>
        <DrawerHeader>
          <span>Menu</span>
          <CloseButton aria-label="Close navigation menu" onClick={onClose}>
            <EnterpriseIcon icon={CancelIcon} size="lg" />
          </CloseButton>
        </DrawerHeader>

        {/* Account Selector - Dropdown style */}
        {accounts && accounts.length > 0 && (
          <SideNavSection>
            <SideNavTitle>Account</SideNavTitle>
            <SideNavAccountButton
              onClick={() => setIsAccountDropdownOpen(!isAccountDropdownOpen)}
              $isOpen={isAccountDropdownOpen}
              aria-expanded={isAccountDropdownOpen}
              aria-haspopup="listbox"
            >
              <span>{displayName}</span>
              <EnterpriseIcon icon={ChevronDownIcon} size="sm" />
            </SideNavAccountButton>
            <SideNavAccountDropdown $isOpen={isAccountDropdownOpen}>
              <SideNavSearchContainer>
                <EnterpriseIcon icon={SearchIcon} size="sm" />
                <SideNavSearchInput
                  type="text"
                  placeholder="Search accounts"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  aria-label="Search accounts"
                />
              </SideNavSearchContainer>
              {filteredAccounts.length > 0 ? (
                filteredAccounts.map((account: any) => (
                  <SideNavAccountItem
                    key={account.id}
                    $isSelected={selectedAccount?.id === account.id}
                    onClick={() => handleAccountSelect(account)}
                  >
                    {getAccountLabel(account)}
                  </SideNavAccountItem>
                ))
              ) : (
                <SideNavNoResults>No accounts found</SideNavNoResults>
              )}
            </SideNavAccountDropdown>
          </SideNavSection>
        )}

        {/* Navigation Links */}
        <SideNavSection>
          <SideNavTitle>Navigation</SideNavTitle>
          {visibleTabs.map((tab) => renderTab(tab))}
        </SideNavSection>
      </Drawer>
    </>
  )
}

export default SideNav
